/* eslint-disable no-nested-ternary */
import { useTranslation } from '@pulsex/localization'
import {
  Box,
  Breadcrumbs,
  Button,
  ButtonMenu,
  ButtonMenuItem,
  Card,
  Flex,
  Heading,
  HelpIcon,
  LinkExternal,
  LogoLoader,
  SubMenuItems,
  Text,
  useMatchBreakpoints,
  useTooltip,
} from '@pulsex/uikit'
import { CurrencyLogo, MultiCurrencyLogo } from '@pulsex/widgets-internal'
import Page from 'components/Layout/Page'
import { useState, useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  usePoolChartDataQuery,
  usePoolDatasQuery,
  usePoolTransactionsQuery,
} from 'state/info/hooks'
import { useCurrency } from 'hooks/Tokens'
import useInfoUserSavedTokensAndPools from 'hooks/useInfoUserSavedTokensAndPoolsList'
import { getBlockExplorerLink, getBlockExploreName } from 'utils'
import ChartCard from 'pages/Info/components/InfoCharts/ChartCard'
import TransactionTable from 'pages/Info/components/InfoTables/TransactionsTable'
import TransactionTableStable from 'pages/Info/components/InfoTables/TransactionsTableStable'
import Percent from 'pages/Info/components/Percent'
import SaveIcon from 'pages/Info/components/SaveIcon'
import { formatAmount } from 'utils/formatInfoNumbers'
import { useActiveChainId } from 'hooks/useActiveChainId'
import InfoNav from '../components/InfoNav'

const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  grid-gap: 1em;
  margin-top: 16px;
  @media screen and (max-width: 800px) {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr 1fr;
  }
`

const TokenButton = styled(Flex)`
  padding: 0px;
  :hover {
    cursor: pointer;
    opacity: 0.6;
  }
`

const LockedTokensContainer = styled(Flex)`
  border: 1px solid ${({ theme }) => theme.colors.cardBorder};
  background-color: ${({ theme }) => theme.colors.backgroundAlt2};
  padding: 16px;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  border-radius: 10px;
  max-width: 280px;
`

const PoolPage: React.FC<{ protocol: string }> = ({ protocol }) => {
  const { routeAddress = '' } = useParams()
  const { isXs, isSm } = useMatchBreakpoints()
  const { t } = useTranslation()
  const { chainId } = useActiveChainId()

  const [showWeeklyData, setShowWeeklyData] = useState(0)
  const { tooltip, tooltipVisible, targetRef } = useTooltip(
    t(`Based on last 7 days' performance. Does not account for impermanent loss`),
    {},
  )

  // In case somebody pastes checksummed address into url (since GraphQL expects lowercase address)
  const address = routeAddress.toLowerCase()

  const poolData = usePoolDatasQuery(protocol, useMemo(() => [address], [address]))[0]
  const chartData = usePoolChartDataQuery(protocol, address)
  const transactions = usePoolTransactionsQuery(protocol, address)

  const token0 = useCurrency(poolData?.token0?.address) ?? undefined
  const token1 = useCurrency(poolData?.token1?.address) ?? undefined
  const token2 = useCurrency(poolData?.token2?.address) ?? undefined

  const { v1, v2, stable } = useInfoUserSavedTokensAndPools(chainId)
  const { savedPools, addPool } = protocol === 'stable' ? stable : protocol === 'v2' ? v2 : v1

  const hasSmallDifference = useMemo(() => {
    return poolData ? Math.abs(poolData.token1Price - poolData.token0Price) < 1 : false
  }, [poolData])

  return (
    <Page>
      <SubMenuItems
        items={[
          {
            label: t('V1'),
            href: `#/info`,
          },
          {
            label: t('V2'),
            href: `#/info/v2`,
          },
          {
            label: t('Stable'),
            href: `#/info/stable`,
          },
        ]}
        activeItem={protocol === 'v2' ? `#/info/v2` : protocol === 'stable' ? `#/info/stable` : `#/info`}
      />
      <InfoNav protocol={protocol} />
      {poolData ? (
        <>
          <Flex justifyContent="space-between" mb="16px" flexDirection={['column', 'column', 'row']}>
            <Breadcrumbs mb="32px">
              <Link to={protocol === 'v1' ? '/info' : `/info/${protocol}`}>
                <Text color="primary">{t('Info')}</Text>
              </Link>
              <Link to={protocol === 'v1' ? '/info/pools' : `/info/${protocol}/pools`}>
                <Text color="primary">{t('Pools')}</Text>
              </Link>
              <Flex>
                <Text mr="8px">{`${token0?.symbol} / ${token1?.symbol}${token2 ? ` / ${token2.symbol}` : ''}`}</Text>
              </Flex>
            </Breadcrumbs>
            <Flex justifyContent={[null, null, 'flex-end']} mt={['8px', '8px', 0]}>
              <LinkExternal mr="8px" to={getBlockExplorerLink(address, 'address', chainId)}>
                {t(`View on ${getBlockExploreName(chainId)}`)}
              </LinkExternal>
              <SaveIcon fill={savedPools.includes(address)} onClick={() => addPool(address)} />
            </Flex>
          </Flex>
          <Flex flexDirection="column">
            <Flex alignItems="center" mb={['8px', null]}>
              <MultiCurrencyLogo currency0={token0} currency1={token1} currency2={token2} size={32} />
              <Text
                ml="38px"
                bold
                fontSize={isXs || isSm ? '24px' : '40px'}
                id="info-pool-pair-title"
              >{`${token0?.symbol} / ${token1?.symbol}${token2 ? ` / ${token2.symbol}` : ''}`}</Text>
            </Flex>


            <Flex justifyContent="space-between" flexDirection={['column', 'column', 'column', 'row']}>
              <Flex alignItems="center" flexDirection={['column', 'column', 'row']} mb={['8px', '8px', null]}>

                <Link to={protocol === 'v1' ? `/info/token/${token0?.wrapped.address}` : `/info/${protocol}/token/${token0?.wrapped.address}`}>
                  <TokenButton>
                    <CurrencyLogo currency={token0} size="24px" />
                    <Text fontSize="16px" ml="4px" style={{ whiteSpace: 'nowrap' }} width="fit-content">
                      {`1 ${token0?.symbol}`}
                    </Text>
                  </TokenButton>
                </Link>

                <Text fontSize="16px" style={{ whiteSpace: 'nowrap' }} width="fit-content" px="10px">=</Text>

                <Link to={protocol === 'v1' ? `/info/token/${token1?.wrapped.address}` : `/info/${protocol}/token/${token1?.wrapped.address}`}>
                  <TokenButton>
                    <CurrencyLogo currency={token1} size="24px" />
                    <Text fontSize="16px" ml="4px" style={{ whiteSpace: 'nowrap' }} width="fit-content">
                      {`${formatAmount(poolData.token1Price, {
                        notation: 'standard',
                        displayThreshold: 0.001,
                        tokenPrecision: hasSmallDifference ? 'enhanced' : 'normal',
                      })} ${token1?.symbol}`}
                    </Text>
                  </TokenButton>
                </Link>

                {token2 && (
                  <>
                    <Text fontSize="16px" style={{ whiteSpace: 'nowrap' }} width="fit-content" px="10px">=</Text>
                    <Link to={protocol === 'v1' ? `/info/token/${token2?.wrapped.address}` : `/info/${protocol}/token/${token2?.wrapped.address}`}>
                      <TokenButton>
                        <CurrencyLogo currency={token2} size="24px" />
                        <Text fontSize="16px" ml="4px" style={{ whiteSpace: 'nowrap' }} width="fit-content">
                          {`${formatAmount(poolData.token2Price, {
                            notation: 'standard',
                            displayThreshold: 0.001,
                            tokenPrecision: hasSmallDifference ? 'enhanced' : 'normal',
                          })} ${token2?.symbol}`}
                        </Text>
                      </TokenButton>
                    </Link>
                  </>
                )}

              </Flex>
              <Flex>
                <Link to={protocol === 'stable' ? `/add/stable/${poolData.address}` : `/add/${protocol}/${poolData.token0.address}/${poolData.token1.address}`}>
                  <Button mr="8px" variant="secondary">
                    {t('Add Liquidity')}
                  </Button>
                </Link>
                <Link
                  to={`/?inputCurrency=${poolData.token0.address}&outputCurrency=${poolData.token1.address}`}
                >
                  <Button>{t('Trade')}</Button>
                </Link>
              </Flex>
            </Flex>
          </Flex>
          <ContentLayout>
            <Box>
              <Card>
                <Box p="24px">
                  <Flex justifyContent="space-between">
                    <Flex flex="1" flexDirection="column">
                      <Text color="secondary" bold fontSize="12px" textTransform="uppercase">
                        {t('Liquidity')}
                      </Text>
                      <Text fontSize="24px" bold>
                        ${formatAmount(poolData.liquidityUSD)}
                      </Text>
                      <Percent value={poolData.liquidityUSDChange} />
                    </Flex>
                    <Flex flex="1" flexDirection="column">
                      <Text color="secondary" bold fontSize="12px" textTransform="uppercase">
                        {t('LP reward APR')}
                      </Text>
                      <Text fontSize="24px" bold>
                        {formatAmount(poolData.lpApr7d)}%
                      </Text>
                      <Flex alignItems="center">
                        <span ref={targetRef}>
                          <HelpIcon color="textSubtle" />
                        </span>
                        <Text ml="4px" fontSize="12px" color="textSubtle">
                          {t('7D performance')}
                        </Text>
                        {tooltipVisible && tooltip}
                      </Flex>
                    </Flex>
                  </Flex>
                  <Text color="secondary" bold mt="24px" fontSize="12px" textTransform="uppercase">
                    {t('Total Tokens Locked')}
                  </Text>
                  <LockedTokensContainer>
                    <Flex justifyContent="space-between">
                      <Flex>
                        <CurrencyLogo currency={token0} size="24px" />
                        <Text small color="textSubtle" ml="8px">
                          {poolData.token0.symbol}
                        </Text>
                      </Flex>
                      <Text small>{formatAmount(poolData.liquidityToken0)}</Text>
                    </Flex>
                    <Flex justifyContent="space-between">
                      <Flex>
                        <CurrencyLogo currency={token1} size="24px" />
                        <Text small color="textSubtle" ml="8px">
                          {poolData.token1.symbol}
                        </Text>
                      </Flex>
                      <Text small>{formatAmount(poolData.liquidityToken1)}</Text>
                    </Flex>
                    {token2 && <Flex justifyContent="space-between">
                      <Flex>
                        <CurrencyLogo currency={token2} size="24px" />
                        <Text small color="textSubtle" ml="8px">
                          {poolData.token2.symbol}
                        </Text>
                      </Flex>
                      <Text small>{formatAmount(poolData.liquidityToken2)}</Text>
                    </Flex>}
                  </LockedTokensContainer>
                </Box>
              </Card>
              <Card mt="16px">
                <Flex flexDirection="column" p="24px">
                  <ButtonMenu
                    activeIndex={showWeeklyData}
                    onItemClick={(index) => setShowWeeklyData(index)}
                    scale="sm"
                    variant="subtle"
                  >
                    <ButtonMenuItem width="100%">{t('24H')}</ButtonMenuItem>
                    <ButtonMenuItem width="100%">{t('7D')}</ButtonMenuItem>
                  </ButtonMenu>
                  <Flex mt="24px">
                    <Flex flex="1" flexDirection="column">
                      <Text color="secondary" fontSize="12px" bold textTransform="uppercase">
                        {showWeeklyData ? t('Volume 7D') : t('Volume 24H')}
                      </Text>
                      <Text fontSize="24px" bold>
                        ${showWeeklyData ? formatAmount(poolData.volumeUSDWeek) : formatAmount(poolData.volumeUSD)}
                      </Text>
                      <Percent value={showWeeklyData ? poolData.volumeUSDChangeWeek : poolData.volumeUSDChange} />
                    </Flex>
                    <Flex flex="1" flexDirection="column">
                      <Text color="secondary" fontSize="12px" bold textTransform="uppercase">
                        {showWeeklyData ? t('LP reward fees 7D') : t('LP reward fees 24H')}
                      </Text>
                      <Text fontSize="24px" bold>
                        ${showWeeklyData ? formatAmount(poolData.lpFees7d) : formatAmount(poolData.lpFees24h)}
                      </Text>
                      <Text color="textSubtle" fontSize="12px">
                        {t('out of $%totalFees% total fees', {
                          totalFees: showWeeklyData
                            ? formatAmount(poolData.totalFees7d)
                            : formatAmount(poolData.totalFees24h),
                        })}
                      </Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            </Box>
            <ChartCard variant="pool" chartData={chartData} />
          </ContentLayout>
          <Heading mb="16px" mt="40px" scale="lg">
            {t('Transactions')}
          </Heading>
          {protocol === 'stable' ? <TransactionTableStable transactions={transactions} /> : <TransactionTable transactions={transactions} />}
        </>
      ) : (
        <Flex mt="80px" justifyContent="center">
          <LogoLoader />
        </Flex>
      )}
    </Page>
  )
}

export default PoolPage
